import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchServersAPI } from '../services/serverService';

export default function GlobalDirectoryPage() {
    const location = useLocation();
    const navigate = useNavigate();
    
    // Default to 'staff' unless 'users' was passed via router state
    const [activeTab, setActiveTab] = useState(location.state?.tab || 'staff');
    const [servers, setServers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadDirectory = async () => {
            try {
                const response = await fetchServersAPI();
                let serverData = response?.data?.data || response?.data || response;
                if (serverData && Array.isArray(serverData.servers)) serverData = serverData.servers;
                else if (!Array.isArray(serverData)) serverData = [];
                
                // Filter out non-active servers for the directory
                setServers(serverData.filter(s => s.status === 'active'));
            } catch (error) {
                console.error("Error loading directory:", error);
            } finally {
                setLoading(false);
            }
        };
        loadDirectory();
    }, []);

    if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>Loading Directory...</div>;

    return (
        <div style={{ padding: '40px', backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
                <button onClick={() => navigate('/dashboard')} style={{ marginRight: '20px', padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>← Dashboard</button>
                <div>
                    <h1 style={{ margin: 0, fontSize: '24px', color: '#2c3e50' }}>Global Directory</h1>
                    <p style={{ margin: '5px 0 0', color: '#666', fontSize: '14px' }}>Platform-wide breakdown of all personnel and clients.</p>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #e2e8f0', paddingBottom: '10px' }}>
                <button 
                    onClick={() => setActiveTab('staff')}
                    style={{ padding: '10px 20px', background: activeTab === 'staff' ? '#2b6cb0' : 'transparent', color: activeTab === 'staff' ? 'white' : '#4a5568', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.2s' }}
                >
                    Workforce (Staff)
                </button>
                <button 
                    onClick={() => setActiveTab('users')}
                    style={{ padding: '10px 20px', background: activeTab === 'users' ? '#805ad5' : 'transparent', color: activeTab === 'users' ? 'white' : '#4a5568', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.2s' }}
                >
                    Service Users
                </button>
            </div>

            {/* Data Table */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead style={{ backgroundColor: '#f7fafc', color: '#718096', fontSize: '13px', textTransform: 'uppercase' }}>
                        <tr>
                            <th style={{ padding: '15px 20px', borderBottom: '1px solid #e2e8f0' }}>Franchise / Server</th>
                            <th style={{ padding: '15px 20px', borderBottom: '1px solid #e2e8f0' }}>{activeTab === 'staff' ? 'Total Staff' : 'Total Service Users'}</th>
                            {activeTab === 'staff' && <th style={{ padding: '15px 20px', borderBottom: '1px solid #e2e8f0' }}>Breakdown</th>}
                            <th style={{ padding: '15px 20px', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {servers.map(server => {
                            const totalAdmins = server.total_admins || 0;
                            const totalEmployees = server.total_employees || 0;
                            const totalStaff = totalAdmins + totalEmployees;
                            const totalUsers = server.total_users || 0;

                            // Skip rendering row if viewing a tab where this server has 0 entries
                            if (activeTab === 'staff' && totalStaff === 0) return null;
                            if (activeTab === 'users' && totalUsers === 0) return null;

                            return (
                                <tr key={server.server_id || server.id} style={{ borderBottom: '1px solid #edf2f7' }}>
                                    <td style={{ padding: '15px 20px', fontWeight: 'bold', color: '#2d3748' }}>
                                        {server.server_name || server.companyName || server.id}
                                    </td>
                                    
                                    <td style={{ padding: '15px 20px', color: '#4a5568', fontWeight: 'bold' }}>
                                        {activeTab === 'staff' ? totalStaff : totalUsers}
                                    </td>
                                    
                                    {activeTab === 'staff' && (
                                        <td style={{ padding: '15px 20px', color: '#718096', fontSize: '13px' }}>
                                            {totalAdmins} Admins, {totalEmployees} Employees
                                        </td>
                                    )}

                                    <td style={{ padding: '15px 20px', textAlign: 'right' }}>
                                        <button 
                                            onClick={() => navigate(`/servers/${server.server_id || server.id}/business-development`)}
                                            style={{ padding: '6px 12px', background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', transition: 'background 0.2s' }}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#bee3f8'} 
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#ebf8ff'}
                                        >
                                            View Analytics →
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                
                {/* Fallback if all active servers have 0 staff/users for the selected tab */}
                {servers.filter(s => activeTab === 'staff' ? ((s.total_admins || 0) + (s.total_employees || 0)) > 0 : (s.total_users || 0) > 0).length === 0 && (
                    <div style={{ padding: '30px', textAlign: 'center', color: '#718096' }}>
                        No active servers found with {activeTab === 'staff' ? 'staff' : 'service users'} assigned.
                    </div>
                )}
            </div>
        </div>
    );
}